'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePersistedView } from 'src/features/data-table-views/use-persisted-view';
import { getLocalStorageValue, setLocalStorageValue } from 'src/utils/local-storage';

import type { OverviewRailPreference } from './overview-rail-preference';
import {
  EMPTY_OVERVIEW_RAIL_PREFERENCE,
  overviewRailPreferenceEquals,
  parseOverviewRailPreference,
} from './overview-rail-preference';

// Collapsing every panel is four toggles inside a second and the payload is the whole object, so
// collapse coalesces where a reorder — one arrow click at a time — writes straight through.
export const COLLAPSE_WRITE_DEBOUNCE_MS = 500;

// The unmount flush runs after the component is gone, where Apollo's lazy-query promise is not
// guaranteed to settle. Losing the cross-device merge on that one path beats losing the write.
const POST_UNMOUNT_READ_TIMEOUT_MS = 1_000;

// A failed read re-arms itself, and the callbacks it depends on change identity as Apollo's state
// moves, so an unbounded retry could spin one round trip per render while the server is down.
const MAX_READ_ATTEMPTS = 3;

export type UseOverviewRailPreferenceResult = {
  /** Empty when nothing is saved, which is the rail's cue to use its own default order. */
  readonly order: readonly string[];
  readonly collapsed: Readonly<Record<string, boolean>>;
  /** Empty when nothing is saved, which is the rail's cue that no section is hidden. */
  readonly hidden: Readonly<Record<string, boolean>>;
  /** Empty when nothing is saved, which is the nav's cue to use its own default tab order. */
  readonly tabOrder: readonly string[];
  /**
   * False until the `user_saved_views` read has settled. A caller that decides something *once*
   * from the saved value — the household landing tab — has to wait for this rather than act on the
   * localStorage seed, which is empty on a device the advisor has not customized from. True
   * immediately when there is no remote persistence to wait for, and on a failed read, where
   * localStorage is all there will ever be.
   */
  readonly isSettled: boolean;
  /** `undefined` when the advisor has never chosen, which the household shell reads as expanded. */
  readonly relationshipExpanded: boolean | undefined;
  readonly setOrder: (order: readonly string[]) => void;
  readonly setPanelCollapsed: (key: string, collapsed: boolean) => void;
  readonly setRelationshipExpanded: (expanded: boolean) => void;
  /**
   * Commits `order`/`hidden`/`tabOrder` in one write. Resolves `true` once that write settles (or
   * immediately, with no remote persistence to fail), `false` on rejection — the caller's cue to
   * toast and keep its draft.
   */
  readonly saveViewConfig: (next: {
    readonly order: readonly string[];
    readonly hidden: Readonly<Record<string, boolean>>;
    readonly tabOrder: readonly string[];
  }) => Promise<boolean>;
};

type LocallyTouched = {
  order: boolean;
  tabOrder: boolean;
  relationshipExpanded: boolean;
  readonly collapsedKeys: Set<string>;
  readonly hiddenKeys: Set<string>;
};

function newLocallyTouched(): LocallyTouched {
  return {
    order: false,
    tabOrder: false,
    relationshipExpanded: false,
    collapsedKeys: new Set(),
    hiddenKeys: new Set(),
  };
}

function resolveNullAfter(ms: number): Promise<null> {
  return new Promise((resolve) => {
    setTimeout(() => resolve(null), ms);
  });
}

// Per field in both directions: the read merges under what this session touched, and the write
// merges what this session touched over a freshly read server value. Without it, the whole-object
// payload means a change made on another device is erased by an unrelated change here.
function mergeTouchedOver(
  base: OverviewRailPreference,
  local: OverviewRailPreference,
  touched: LocallyTouched,
): OverviewRailPreference {
  const collapsed: Record<string, boolean> = { ...base.collapsed };
  for (const key of touched.collapsedKeys) {
    const flag = local.collapsed[key];
    if (flag !== undefined) {
      collapsed[key] = flag;
    }
  }
  const hidden: Record<string, boolean> = { ...base.hidden };
  for (const key of touched.hiddenKeys) {
    const flag = local.hidden[key];
    if (flag !== undefined) {
      hidden[key] = flag;
    }
  }
  return {
    order: touched.order ? local.order : base.order,
    collapsed,
    hidden,
    tabOrder: touched.tabOrder ? local.tabOrder : base.tabOrder,
    relationshipExpanded: touched.relationshipExpanded ? local.relationshipExpanded : base.relationshipExpanded,
  };
}

/**
 * The advisor's rail layout for one surface, seeded synchronously from localStorage so an
 * already-visited device paints the saved order rather than reshuffling the rail a frame later,
 * then reconciled against `user_saved_views`, which is the source of truth for anything this
 * session has not touched. Both persistence directions swallow failures: a GraphQL outage degrades
 * the layout to per-device rather than breaking the page.
 *
 * No `enabled` gate. The household instance is mounted by `ClientHouseholdLayout`, so it costs one
 * `userSavedViews` read per household route load even where no rail renders.
 */
export function useOverviewRailPreference({
  storageKey,
}: {
  readonly storageKey: string;
}): UseOverviewRailPreferenceResult {
  const { hasRemotePersistence, getSavedViewsByKeys, upsertSavedViews } = usePersistedView();

  // Carries its key so a surface that swaps `storageKey` mid-life falls back to the new seed
  // instead of rendering the previous surface's layout until the read lands.
  const [applied, setApplied] = useState<{
    readonly storageKey: string;
    readonly value: OverviewRailPreference;
  } | null>(null);

  const [settledKey, setSettledKey] = useState<string | null>(null);

  const seedRef = useRef<{ readonly storageKey: string; readonly value: OverviewRailPreference | null } | null>(null);
  const touchedRef = useRef<LocallyTouched>(newLocallyTouched());
  const hasReadRef = useRef(false);
  const readAttemptsRef = useRef(0);
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  if (seedRef.current?.storageKey !== storageKey) {
    seedRef.current = {
      storageKey,
      value: parseOverviewRailPreference(getLocalStorageValue({ dataKey: storageKey })),
    };
    touchedRef.current = newLocallyTouched();
    hasReadRef.current = false;
    readAttemptsRef.current = 0;
    // The write closure and `touchedRef` are both key-scoped, so a timer armed for the previous
    // surface would send the new key a payload built from nothing it touched. The mirror still
    // holds that toggle, so the old surface picks it up again on its next change.
    if (collapseTimerRef.current !== null) {
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }
  }

  const preference =
    (applied?.storageKey === storageKey ? applied.value : null) ??
    seedRef.current.value ??
    EMPTY_OVERVIEW_RAIL_PREFERENCE;
  const preferenceRef = useRef(preference);

  useEffect(() => {
    preferenceRef.current = preference;
  }, [preference]);

  // Serialised because the payload is the whole object, not a delta: two writes in flight at once
  // can commit out of order and leave the server a change behind.
  const pendingWriteRef = useRef<Promise<unknown>>(Promise.resolve());
  const queueRemoteWrite = useCallback(
    ({ afterUnmount = false }: { readonly afterUnmount?: boolean } = {}): Promise<boolean> => {
      if (!hasRemotePersistence) return Promise.resolve(true);

      async function write() {
        let base = preferenceRef.current;
        try {
          const read = getSavedViewsByKeys({ keys: [storageKey] });
          const remoteValues = afterUnmount
            ? await Promise.race([read, resolveNullAfter(POST_UNMOUNT_READ_TIMEOUT_MS)])
            : await read;
          if (remoteValues !== null) {
            base = parseOverviewRailPreference(remoteValues.get(storageKey)) ?? base;
          }
        } catch {
          // Losing another device's layout beats dropping the advisor's own click.
        }

        const value = mergeTouchedOver(base, preferenceRef.current, touchedRef.current);
        return upsertSavedViews({ entries: [{ key: storageKey, value }] });
      }

      // Handed back so a Save can report failure; the chain still swallows it so one failure can't
      // wedge a later, unrelated write.
      const outcome = pendingWriteRef.current.then(write, write);
      pendingWriteRef.current = outcome.catch(() => undefined);
      return outcome.then(
        () => true,
        () => false,
      );
    },
    [getSavedViewsByKeys, hasRemotePersistence, storageKey, upsertSavedViews],
  );

  // Read through a ref: the mutation hook returns a fresh `upsertSavedViews` on every state change,
  // so depending on it directly would re-run the read effect below on the first change and tear
  // down the in-flight read the merge depends on.
  const queueRemoteWriteRef = useRef(queueRemoteWrite);
  useEffect(() => {
    queueRemoteWriteRef.current = queueRemoteWrite;
  }, [queueRemoteWrite]);

  const cancelPendingCollapseWrite = useCallback(() => {
    if (collapseTimerRef.current === null) return false;
    clearTimeout(collapseTimerRef.current);
    collapseTimerRef.current = null;
    return true;
  }, []);

  // A collapse toggled and then navigated away from inside the debounce window would otherwise be
  // lost, and the rail unmounts on every household switch.
  useEffect(
    () => () => {
      if (cancelPendingCollapseWrite()) {
        queueRemoteWriteRef.current({ afterUnmount: true });
      }
    },
    [cancelPendingCollapseWrite],
  );

  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  useEffect(() => {
    // A one-shot bootstrap: writes re-read for themselves, so re-reading here would only re-fetch
    // a value this device just sent.
    if (!hasRemotePersistence || hasReadRef.current) return;
    hasReadRef.current = true;
    readAttemptsRef.current += 1;

    void (async () => {
      try {
        const remoteValues = await getSavedViewsByKeys({ keys: [storageKey] });
        // The key is compared as well as the mount: this continuation closes over the key it was
        // started for, and letting it through after a swap would land the previous surface's layout
        // in `preferenceRef`, which the next write persists under the new key.
        if (unmountedRef.current || seedRef.current?.storageKey !== storageKey) return;

        const remote = parseOverviewRailPreference(remoteValues.get(storageKey));
        if (remote !== null) {
          const merged = mergeTouchedOver(remote, preferenceRef.current, touchedRef.current);
          if (!overviewRailPreferenceEquals(merged, preferenceRef.current)) {
            preferenceRef.current = merged;
            setApplied({ storageKey, value: merged });
            setLocalStorageValue<OverviewRailPreference>({ dataKey: storageKey, data: merged });
          }
        }
      } catch {
        // Best effort — localStorage carries the layout, and clearing the flag lets a later dep
        // change retry instead of stranding the session on one transient failure.
        if (readAttemptsRef.current < MAX_READ_ATTEMPTS) {
          hasReadRef.current = false;
        }
      }
      // Settles even on a failed read: a retry only fires if a dependency happens to change, so
      // waiting on one would leave `isSettled` false forever on a server that stays down. The cost
      // is that a retry landing after a one-shot consumer has already decided delivers a value that
      // consumer never sees — which fails safe, since an empty `tabOrder` means "stay on Overview".
      if (unmountedRef.current || seedRef.current?.storageKey !== storageKey) return;
      setSettledKey(storageKey);
    })();
  }, [getSavedViewsByKeys, hasRemotePersistence, storageKey]);

  const apply = useCallback(
    (next: OverviewRailPreference) => {
      preferenceRef.current = next;
      setApplied({ storageKey, value: next });
      setLocalStorageValue<OverviewRailPreference>({ dataKey: storageKey, data: next });
    },
    [storageKey],
  );

  const setOrder = useCallback(
    (order: readonly string[]) => {
      touchedRef.current.order = true;
      apply({ ...preferenceRef.current, order });
      // The write sends the whole object, so it already carries any debounced collapse change.
      cancelPendingCollapseWrite();
      queueRemoteWriteRef.current();
    },
    [apply, cancelPendingCollapseWrite],
  );

  const setPanelCollapsed = useCallback(
    (key: string, collapsed: boolean) => {
      const current = preferenceRef.current;
      touchedRef.current.collapsedKeys.add(key);
      apply({ ...current, collapsed: { ...current.collapsed, [key]: collapsed } });

      cancelPendingCollapseWrite();
      collapseTimerRef.current = setTimeout(() => {
        collapseTimerRef.current = null;
        queueRemoteWriteRef.current();
      }, COLLAPSE_WRITE_DEBOUNCE_MS);
    },
    [apply, cancelPendingCollapseWrite],
  );

  const setRelationshipExpanded = useCallback(
    (expanded: boolean) => {
      touchedRef.current.relationshipExpanded = true;
      apply({ ...preferenceRef.current, relationshipExpanded: expanded });
      // Straight through rather than debounced like collapse: one click swaps the whole main column,
      // and the payload it sends already carries any pending collapse toggle.
      cancelPendingCollapseWrite();
      queueRemoteWriteRef.current();
    },
    [apply, cancelPendingCollapseWrite],
  );

  const saveViewConfig = useCallback(
    (next: {
      readonly order: readonly string[];
      readonly hidden: Readonly<Record<string, boolean>>;
      readonly tabOrder: readonly string[];
    }) => {
      const current = preferenceRef.current;
      touchedRef.current.order = true;
      touchedRef.current.tabOrder = true;
      for (const key of Object.keys(next.hidden)) {
        touchedRef.current.hiddenKeys.add(key);
      }
      apply({
        ...current,
        order: next.order,
        hidden: { ...current.hidden, ...next.hidden },
        tabOrder: next.tabOrder,
      });
      cancelPendingCollapseWrite();
      return queueRemoteWriteRef.current();
    },
    [apply, cancelPendingCollapseWrite],
  );

  return {
    order: preference.order,
    collapsed: preference.collapsed,
    hidden: preference.hidden,
    tabOrder: preference.tabOrder,
    isSettled: !hasRemotePersistence || settledKey === storageKey,
    relationshipExpanded: preference.relationshipExpanded,
    setOrder,
    setPanelCollapsed,
    setRelationshipExpanded,
    saveViewConfig,
  };
}
